/**
 * Local design-preview mock saved searches for the Saved Search list page.
 * Used in development so the list has visible rows without depending on
 * authenticated workspace API cookies (e.g. REACT_APP_DEV_AUTH_BYPASS).
 */

import { MOCK_SEARCH_COMPANIES } from "./mockSearchCompanies";

export const MOCK_SAVED_SEARCHES = [
  {
    id: "mock-saved-midwest",
    name: "Midwest software targets",
    company_count: 24,
    created_at: "2026-06-01T12:00:00Z",
    updated_at: "2026-06-01T12:30:00Z",
  },
  {
    id: "mock-saved-logistics",
    name: "Logistics follow-ups",
    company_count: 12,
    created_at: "2026-05-28T15:20:00Z",
    updated_at: "2026-05-28T15:20:00Z",
  },
  {
    id: "mock-saved-healthcare",
    name: "Healthcare growth pipeline",
    company_count: 37,
    created_at: "2026-05-20T09:10:00Z",
    updated_at: "2026-05-22T11:00:00Z",
  },
  {
    id: "mock-saved-saas",
    name: "Series B SaaS prospects",
    company_count: 18,
    created_at: "2026-05-12T18:45:00Z",
    updated_at: "2026-05-14T08:05:00Z",
  },
  {
    id: "mock-saved-manufacturing",
    name: "Manufacturing expansion leads",
    company_count: 9,
    created_at: "2026-04-30T13:00:00Z",
    updated_at: "2026-05-01T10:15:00Z",
  },
];

export const isMockSavedSearchesEnabled = () =>
  process.env.REACT_APP_USE_MOCK_SAVED_SEARCHES === "true";

export const isMockSavedSearchId = (id) =>
  String(id || "").startsWith("mock-saved-");

/** Session-only rows created via Save Search / Create Workspace in local preview. */
let sessionCreatedSearches = [];

export const getMockSavedSearches = () => [
  ...sessionCreatedSearches,
  ...MOCK_SAVED_SEARCHES,
];

export const resetMockSavedSearchesSession = () => {
  sessionCreatedSearches = [];
};

/**
 * Persist a saved search locally so design-preview Save Search works without
 * a running workspace API. Shape matches POST /workspaces success.
 */
/**
 * @param {{ name?: string, query?: object, company_ids?: string[] }} input
 */
export const createMockSavedSearch = ({ name, query, company_ids } = {}) => {
  const trimmedName = typeof name === "string" ? name.trim() : "";
  if (!trimmedName) {
    return {
      ok: false,
      status: 422,
      detail: "Invalid workspace configuration provided.",
    };
  }

  const duplicate = getMockSavedSearches().some(
    (workspace) => String(workspace.name || "").toLowerCase() === trimmedName.toLowerCase()
  );
  if (duplicate) {
    return {
      ok: false,
      status: 409,
      detail: "A saved search with this name already exists.",
    };
  }

  const now = new Date().toISOString();
  const companyCount = Array.isArray(company_ids) && company_ids.length > 0
    ? company_ids.length
    : MOCK_SEARCH_COMPANIES.length;
  const created = {
    id: `mock-saved-${Date.now()}`,
    name: trimmedName,
    company_count: companyCount,
    created_at: now,
    updated_at: now,
    source_query: query && typeof query === "object" ? query : null,
    query: query && typeof query === "object" ? query : null,
  };
  sessionCreatedSearches = [created, ...sessionCreatedSearches];
  return { ok: true, status: 201, data: created };
};

export const deleteMockSavedSearch = (id) => {
  const before = sessionCreatedSearches.length;
  sessionCreatedSearches = sessionCreatedSearches.filter((row) => row.id !== id);
  return sessionCreatedSearches.length !== before;
};

/**
 * Fabricate workspace detail for local mock saved-search routes so the detail
 * page can render the same EnrichmentGrid mock company set as Search.
 */
export const buildMockSavedSearchDetail = (id) => {
  const listed = getMockSavedSearches().find((row) => row.id === id);
  const companies = MOCK_SEARCH_COMPANIES;
  return {
    id,
    name: listed?.name || "Saved search preview",
    company_count: companies.length,
    created_at: listed?.created_at || null,
    updated_at: listed?.updated_at || null,
    source_query: listed?.source_query || listed?.query || {
      TermsInclude: ["software"],
    },
    columns: ["Name", "Domain", "Location"],
    rows: companies.map((company) => ({
      stagedId: company.stagedId,
      Name: company.primaryName,
      Domain: company.primaryWebsite || "",
      Location: company.primaryLocation?.fullAddress || "",
    })),
  };
};
